import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import './StudentVerifyClassCode.scss';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import InputField from '../../../components/InputField/InputField';
import ButtonStyle from '../../../components/ButtonStyle/ButtonStyle';
import { FC, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { VERIFY_CLASS_CODE_STUDENT } from '../api/AdminLoginQuery';
import ToasterWithMessage from '../../../components/toaster';
import { FETCH_POLICY_NO_CACHE, messages, PATH_ADDRESSES, patterns, PTA_ROLE } from '../../../utils/constants';
import { useLocation, useNavigate } from 'react-router-dom';
import { login, logout, setLoginPage, setSelectedClassRoomId, setTeacherMainRoute } from '../../../redux/slices/authSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/app/hooks';
import { signIn } from "aws-amplify/auth"
import WarningPopup from '../../../components/WarningPopup/WarningPopup';
import images from '../../../assets/images';
import { handleInputWithoutSpaces } from '../../../utils/helper';
import { MeetingCategoryType } from '../../AdminPta/AdminPta';
import { VERIFY_GENERAL_CATEGORY, VERIFY_MEETING_CATEGORY } from '../../AdminPta/api/PtaQuery';

interface StudentVerifyClassCodeProps {
    loginType: 'student' | 'pta' | 'ptaVerify',
    selectedCategory?: MeetingCategoryType
}

interface IFormInput{
    passcode: string;
}

//validation
const schema = yup.object().shape({
    passcode: yup.string().required('Code is required')
            .uppercase()
            .length(6, 'Code must be exactly 6 characters')
            .matches(patterns?.onlyAlphaNumeric, messages?.validCode)
});


const StudentVerifyClassCode:FC<StudentVerifyClassCodeProps> = ({ loginType, selectedCategory }) => {
 
  const [disableButton, setDisableButton] = useState<boolean>(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {error} = ToasterWithMessage();
  const methods = useForm<IFormInput>({resolver: yupResolver(schema)});
  const { handleSubmit, setFocus, setValue, reset } = methods;
  const userName = process.env.REACT_APP_PTA_USERNAME;
  const password = process.env.REACT_APP_PTA_PASSWORD;
  const [openWarningPopup, setOpenWarningPopup] = useState<boolean>(false);
  const { sorrygif } = images;
  const role = useAppSelector((state) => state.auth.role);
  const location = useLocation();
  const [studentClassCode, setStudentClassCode] = useState<string>("")

  const [verifyClassCodeForStudentV2] = useLazyQuery(VERIFY_CLASS_CODE_STUDENT, {
    fetchPolicy: FETCH_POLICY_NO_CACHE,
    onCompleted: (data) => {
        if(data?.verifyClassCodeForStudentV2?.cId){
            localStorage.setItem("studentClassCode", studentClassCode || "")
            dispatch(setSelectedClassRoomId({selectedClassRoomId: data?.verifyClassCodeForStudentV2?.cId}));
            navigate(PATH_ADDRESSES?.STUDENT_CLASSROOM_PATH);
        }
    },
    onError: (err) => {
        setDisableButton(false);
        error(err?.message);
    }
  });

  const [verifyGeneralMeetingCode] = useLazyQuery(VERIFY_GENERAL_CATEGORY, {
    fetchPolicy: FETCH_POLICY_NO_CACHE,
    onCompleted: (data) => {
        navigate('/pta-meetings');
        setDisableButton(true);
        localStorage.setItem('isGuest','true');
        localStorage.setItem('saId',data?.verifyGeneralMeetingCode)
    },
    onError: (err) => {
        setDisableButton(false);
        dispatch(logout());
        error(err?.message);
    }
  });

  const [verifyMeetingCategory] = useLazyQuery(VERIFY_MEETING_CATEGORY, {
    fetchPolicy: FETCH_POLICY_NO_CACHE,
    onCompleted: (data) => {
        localStorage.setItem('isVerify','true');
        navigate(role === PTA_ROLE ? '/pta-meetings' : '/teacher-pta', {
            state: {
                category: selectedCategory
            }
        });
        setDisableButton(true);
    },
    onError: (err) => {
        setDisableButton(false);
        setOpenWarningPopup(true);
        setValue('passcode', '');
    }
  });

  const onSubmit: SubmitHandler<IFormInput> = async(data) => {
        setDisableButton(true);
        if (loginType === 'student') {
           setStudentClassCode(data.passcode)
            verifyClassCodeForStudentV2({
                variables: {
                    cCode: data?.passcode
                }
            })
        }
        else if (loginType === 'pta' && userName && password) {
            await handleSignIn(userName,password);
            verifyGeneralMeetingCode({
                variables: {
                    mCode: data?.passcode
                }
            }).then(() => {
                localStorage.setItem('generalCode',data?.passcode);
                localStorage.setItem('isVerify','false');
            })
        }
        else if (loginType === 'ptaVerify') {
            verifyMeetingCategory({
                variables: {
                    mCode: data?.passcode,
                    mcId: selectedCategory?.mcId
                }
            })
        }
  }

  const handleSignIn = async(userName: string, password: string) => {
    await signIn({username: userName,password: password})
    .then((datas) => {
        if(datas?.isSignedIn){
            const accessToken : any =localStorage.getItem(Object.keys(localStorage).find((key) => key.includes('accessToken')) as string) || null;
            const token = JSON.parse(window.atob(accessToken.split('.')[1]));
            const role = token['cognito:groups'][0];
            dispatch(setLoginPage({loginPage: 'pta'}));
            dispatch(login({role: role ? role : null}));
        }
    })
    .catch((err) => {console.log("err while auto signin",err)})
}

  useEffect(() => {
    setFocus('passcode');
  }, [setFocus])

  useEffect(() => {
    setValue('passcode', '');
    reset();
  },[selectedCategory])

  const closeWarnPopup = () => {
    setOpenWarningPopup(false);
  }
  
  return (
    <div className={`studentLoginCtn ${location?.pathname?.includes('/student-verifyCode') ? "studentVerifyCode" : ""}`}>
    <FormProvider {...methods}>
    <div className='verifyMainContainer'>
        <form autoComplete="off" className="classCodeForm" onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className='inputFieldContainer'>
                <div className='labelStyle'>{loginType === 'student' ? 'Join with class code' : 'Join with Passcode'}</div>
                <InputField label="passcode" name="passcode" type="text" size="fullSUpper" onInput={handleInputWithoutSpaces}/>
            </div>
            <div className={`submitButtonContainer ${ loginType === "ptaVerify" && "ptaSubmitButtonContainer"}`}>
                <ButtonStyle disableButton={disableButton} text="Continue" type="submit" width="154px"/>
            </div>
        </form>
    </div>
    <WarningPopup
        message='Sorry this meeting code is Incorrect'
        openWarningPopup={openWarningPopup}
        closeWarnPopup={closeWarnPopup}
        gifData={sorrygif}
    />
    </FormProvider>
    </div>
  )
}

export default StudentVerifyClassCode